import { Autocomplete, TextField } from "@mui/material";
import { useMemo } from "react";
import { AutocompleteActionsContext, AutocompleteActionsPaper } from './autocomplete_actions_paper';
import { useVirtualizedAutocomplete } from './use_virtualized_autocomplete';
import { VirtualizedAutocompleteContext, VirtualizedAutocompleteListbox } from './virtualized_autocomplete_listbox';

type Props<T> = {
  label?: string;
  newButtonLabel?: string;
  value?: T | null;
  options: T[];
  required?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  helperText?: string;
  getOptionLabel: (opt: T) => any;
  getOptionKey: (opt: T) => any;
  getOptionDisabled?: (opt: T) => boolean;
  onNew?: () => void;
  onChange: (newValue: T | null) => void;
};

export const DefaultAutocomplete = <T,>({
  label, newButtonLabel, value, options, required, placeholder,
  readOnly, helperText, getOptionLabel, getOptionKey, getOptionDisabled,
  onNew, onChange,
}: Props<T>) => {
  const shrink = value == null && placeholder != null ? true : undefined;
  const { contextValue, ...virtualizationProps } = useVirtualizedAutocomplete(options, getOptionLabel);
  // Keep the existing requirement for a label to display the New action.
  const actions = useMemo(() => ({ onNew: newButtonLabel ? onNew : undefined, newButtonLabel, disabled: readOnly }),
    [onNew, newButtonLabel, readOnly]);

  return (
    <AutocompleteActionsContext.Provider value={actions}>
      <VirtualizedAutocompleteContext.Provider value={contextValue}>
        <Autocomplete
          {...virtualizationProps}
          disableListWrap
          size="small"
          fullWidth
          getOptionDisabled={getOptionDisabled}
          getOptionLabel={getOptionLabel}
          getOptionKey={getOptionKey}
          options={options}
          readOnly={readOnly}
          slots={{ paper: AutocompleteActionsPaper }}
          slotProps={{ listbox: { component: VirtualizedAutocompleteListbox } }}
          value={value ?? null}
          onChange={(_, newValue) => {
            if (!readOnly) onChange(newValue);
          }}
          renderInput={params => (
            <TextField
              {...params}
              size="small"
              label={label}
              helperText={helperText}
              error={helperText !== undefined}
              slotProps={{ inputLabel: { shrink } }}
              required={required}
              placeholder={placeholder}
            />
          )}
        />
      </VirtualizedAutocompleteContext.Provider>
    </AutocompleteActionsContext.Provider>
  );
};
